import {
  FilterRequest,
  MarketingListDoc,
  type PropertyMetadata,
  MarketingListsResponse,
  AiGenerateFiltersRequest,
  AiGenerateFiltersResponse,
  MarketingListLightResponse,
  PreviewMarketingListRequest,
  PreviewMarketingListResponse,
  MarketingListContactsResponse,
  MarketingListPopulatedResponse,
} from '@/types/entities';
import { ApiService } from './api.service';
import { endpoints } from './endpoints';

interface FilterablePropertiesResponse {
  data: PropertyMetadata[];
  categories: string[];
}

interface ReferenceOptionItem {
  _id: string;
  name: string;
}

interface ReferenceOptionsResponse {
  data: ReferenceOptionItem[];
}

/**
 * Service for handling marketing list-related API calls
 */
export class MarketingListApiService extends ApiService {
  /**
   * Gets all marketing lists (reference data)
   */
  getAll = async (): Promise<MarketingListLightResponse> =>
    this.get(endpoints.marketingLists.all);

  /**
   * Gets filtered marketing lists with pagination
   */
  getFilteredMarketingLists = async (filters: FilterRequest): Promise<MarketingListsResponse> =>
    this.get(endpoints.marketingLists.list, { params: filters });

  /**
   * Deletes a marketing list
   */
  deleteMarketingList = async (id: string): Promise<void> =>
    this.delete(endpoints.marketingLists.delete(id));

  /**
   * Gets a marketing list by ID (backend populates contacts)
   */
  getMarketingListById = async (id: string): Promise<MarketingListPopulatedResponse> =>
    this.get(endpoints.marketingLists.byId(id));

  /**
   * Creates a new marketing list
   */
  createMarketingList = async (data: Partial<MarketingListDoc>): Promise<MarketingListDoc> =>
    this.post(endpoints.marketingLists.create, data);

  /**
   * Updates a marketing list
   */
  updateMarketingList = async (
    id: string,
    data: Partial<MarketingListDoc>
  ): Promise<MarketingListDoc> => this.patch(endpoints.marketingLists.update(id), data);

  /**
   * Gets contacts from a marketing list with pagination
   */
  getMarketingListContacts = async (
    id: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MarketingListContactsResponse> =>
    this.get(`${endpoints.marketingLists.contacts(id)}?page=${page}&pageSize=${pageSize}`);

  /**
   * Manually triggers dynamic lists refresh
   */
  refreshDynamicLists = async (): Promise<{ updated: number }> =>
    this.post(endpoints.marketingLists.refreshDynamic, {});

  /**
   * Refreshes a specific dynamic list
   */
  refreshSingleList = async (id: string): Promise<{ id: string; contactCount: number }> =>
    this.post(endpoints.marketingLists.refresh(id), {});

  /**
   * Previews a marketing list without saving it
   * Returns estimated contact count and sample contacts
   */
  previewMarketingList = async (
    request: PreviewMarketingListRequest
  ): Promise<PreviewMarketingListResponse> =>
    this.post(endpoints.marketingLists.preview, request);

  /**
   * Generates filter criteria using AI from natural language
   */
  aiGenerateFilters = async (
    request: AiGenerateFiltersRequest
  ): Promise<AiGenerateFiltersResponse> => this.post(endpoints.marketingLists.aiGenerate, request);

  /**
   * Gets available filterable properties metadata from backend
   */
  getFilterableProperties = async (): Promise<FilterablePropertiesResponse> =>
    this.get(endpoints.marketingLists.filterableProperties);

  /**
   * Gets reference options for a given collection (e.g., countries, organizations)
   */
  getReferenceOptions = async (endpoint: string): Promise<ReferenceOptionsResponse> =>
    this.get(endpoint);
}

// Export a singleton instance
export const marketingListApiService = new MarketingListApiService();

// Named exports for convenience
export const {
  getAll: getMarketingLists,
  getFilteredMarketingLists,
  deleteMarketingList,
  getMarketingListById,
  createMarketingList,
  updateMarketingList,
  getMarketingListContacts,
  refreshDynamicLists,
  refreshSingleList,
  getFilterableProperties,
  getReferenceOptions,
} = marketingListApiService;
